import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Alert,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { AuthContext } from '../../authContext/context';
import { postRequest } from '../../lib/api';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useForm, Controller } from 'react-hook-form';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => c._id === customerId);
  };

  const handleCreateCustomer = async () => {
    // Check if at least email or phone is provided
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    try {
      setCreating(true);
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || '',
        dob: '',
        address1: '',
        address2: '',
        town: '',
        country: '',
        postcode: ''
      };

      const res = await postRequest('private/createCustomer', payload, authToken);

      if (res?.status === 200 && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Customer created successfully!',
          timer: 1500,
          showConfirmButton: false
        });

        // Add the newly created customer
        onCustomerAdd(res.data);

        // Reset search fields
        setSearchFields({
          title: '',
          forename: '',
          surname: '',
          email: '',
          phone_number: ''
        });

        // Close modal
        onHide();
      } else {
        throw new Error(res?.message || 'Failed to create customer');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to create customer'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Customer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCustomerAdded(customer._id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          {isCustomerAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                          No customers found
                        </Typography>
                        {(searchFields.email || searchFields.phone_number || searchFields.forename || searchFields.surname) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'Add Customer'}
                          </Button>
                        )}
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default function FirstRegistrationDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const authCtx = useContext(AuthContext);

  // Get basicFormData from previous step (SimpleConveyancingForm)
  const basicFormData = location.state?.basicFormData || {};
  console.log('📋 Basic Form Data from Step 1 (First Registration):', basicFormData);

  // Get customerId from URL query params (same as Purchase/Sale & Purchase forms)
  const [searchParams] = useSearchParams();
  // Support both 'customerId' and 'customer_id' parameter names
  const customerIdFromUrl = searchParams.get('customerId') || searchParams.get('customer_id');

  const [conveyancingData, setConveyancingData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [partners, setPartners] = useState([]);

  // Postcode validation states
  const [postcodeValue, setPostcodeValue] = useState('');
  const [checkingPostcode, setCheckingPostcode] = useState(false);
  const [postcodeMatch, setPostcodeMatch] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isPostcodeValid, setIsPostcodeValid] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue
  } = useForm({
    defaultValues: {
      // Property Details
      line1: '',
      line2: '',
      town: '',
      county: '',
      postcode: '',
      propertyValue: '',
      lender_panel_id: '',
      partner_id: ''
    }
  });

  // 2-step postcode validation with branch matching
  useEffect(() => {
    if (!postcodeValue || postcodeValue.trim().length === 0) {
      setIsPostcodeValid(false);
      return;
    }

    const checkPostcode = async () => {
      setCheckingPostcode(true);
      try {
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);

        // STEP 1: Check if postcode exists
        const checkRes = await postRequest(
          'private/checkPostcodeInBranch',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        if (checkRes?.status === 406 || checkRes?.data?.exists === false) {
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'Invalid Postcode',
            text: checkRes?.message || 'This postcode does not exist in the UK postcode database.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
          return;
        }

        // STEP 2: Match branch by postcode
        const matchRes = await postRequest(
          'private/matchBranchByPostcode',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        if (matchRes?.data?.matched) {
          setPostcodeMatch(matchRes.data.branch);
          setIsPostcodeValid(true);
        } else {
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'No Branch Coverage',
            text: 'This postcode area is not currently served by any of our branches.',
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
        }
      } catch (error) {
        setIsPostcodeValid(false);
      } finally {
        setCheckingPostcode(false);
      }
    };

    const timeoutId = setTimeout(checkPostcode, 500);
    return () => clearTimeout(timeoutId);
  }, [postcodeValue, authCtx.token]);

  // Fetch active lender panels
  const fetchActiveLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (response?.status === 200 && response?.data?.docs) {
        setPartners(response.data.docs);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Fetch conveyancing details
  const fetchConveyancingDetails = async () => {
    console.log('🔍 === FETCHING FIRST REGISTRATION DETAILS ===');
    console.log('📍 Lead ID:', id);

    try {
      setLoading(true);

      // Use dedicated First Registration API
      console.log('📊 Fetching First Registration data...');
      const mainLeadResponse = await postRequest(`private/getFirstRegistrationDetails/${id}`, {}, authCtx.token);

      if (mainLeadResponse?.status === 200 && mainLeadResponse?.data) {
        const lead = mainLeadResponse.data;
        console.log('✅ Found main lead data:', lead);
        console.log('👥 Main lead customers:', lead.customers);

        setConveyancingData(lead);

        // Auto-fill Customers from main lead
        console.log('👥 === AUTO-FILLING CUSTOMERS ===');

        let customersToSet = [];

        // Check if customers are already populated (objects) or just IDs
        if (lead.customers && Array.isArray(lead.customers) && lead.customers.length > 0) {
          const firstCustomer = lead.customers[0];

          if (typeof firstCustomer === 'object' && firstCustomer._id) {
            // Already populated
            customersToSet = lead.customers;
            console.log('✅ Using populated customers:', customersToSet.length);
          } else if (typeof firstCustomer === 'string') {
            // Need to fetch
            console.log('🔍 Fetching customers by IDs:', lead.customers);
            try {
              const customerPromises = lead.customers.map(async (customerId) => {
                try {
                  const customerRes = await postRequest(`private/getCustomerIndividualData/${customerId}`, {}, authCtx.token);
                  return customerRes?.data || null;
                } catch (err) {
                  console.log('❌ Error fetching customer', customerId);
                  return null;
                }
              });
              const fetchedCustomers = await Promise.all(customerPromises);
              customersToSet = fetchedCustomers.filter(c => c && c._id);
              console.log('✅ Customers fetched successfully:', customersToSet.length);
            } catch (err) {
              console.log('❌ Error in customer fetching:', err);
            }
          }
        }

        // Set customers from lead data (this will overwrite URL customer if exists)
        if (customersToSet.length > 0) {
          setCustomers(customersToSet);
          console.log('✅ Customers state updated with', customersToSet.length, 'customers');
        } else {
          console.log('⚠️ No customers found in lead data');
        }

        // Populate property details
        console.log('🏠 === AUTO-FILLING PROPERTY DETAILS ===');

        // Auto-fill property address - check multiple possible field names
        const address = lead.conveyancing_details?.propertyDetails?.address
          || lead.propertyDetails?.address
          || lead.property_address
          || lead.address
          || lead.propertyAddress;
        console.log('📍 Checking for property address:', {
          conveyancing_propertyDetails: lead.conveyancing_details?.propertyDetails?.address,
          propertyDetails_address: lead.propertyDetails?.address,
          property_address: lead.property_address,
          address: lead.address,
          propertyAddress: lead.propertyAddress
        });

        if (address) {
          console.log('📍 Property address found:', address);

          if (typeof address === 'object') {
            setValue('line1', address.line1 || address.address1 || address.addressLine1 || '');
            setValue('line2', address.line2 || address.address2 || address.addressLine2 || '');
            setValue('town', address.town || address.city || '');
            setValue('county', address.county || address.state || address.country || '');
            const postcodeValue = address.postcode || address.postal_code || address.post_code || address.zip || '';
            setValue('postcode', postcodeValue);
            setPostcodeValue(postcodeValue); // trigger postcode validation for prefilled value

            console.log('✅ Address fields populated:', {
              line1: address.line1 || address.address1,
              line2: address.line2 || address.address2,
              town: address.town,
              county: address.county,
              postcode: postcodeValue
            });
          } else if (typeof address === 'string') {
            setValue('line1', address);
            console.log('✅ Address populated as string:', address);
          }
        } else {
          console.log('⚠️ No property address found in lead data, checking customer address...');

          // If no property address, try to use customer's address
          if (customersToSet.length > 0 && customersToSet[0]?.address) {
            const customerAddress = customersToSet[0].address;
            console.log('📍 Using customer address:', customerAddress);

            setValue('line1', customerAddress.line1 || customerAddress.address1 || customerAddress.addressLine1 || '');
            setValue('line2', customerAddress.line2 || customerAddress.address2 || customerAddress.addressLine2 || '');
            setValue('town', customerAddress.town || customerAddress.city || '');
            setValue('county', customerAddress.county || customerAddress.state || customerAddress.country || '');
            const customerPostcode = customerAddress.postcode || customerAddress.postal_code || customerAddress.post_code || customerAddress.zip || '';
            setValue('postcode', customerPostcode);
            setPostcodeValue(customerPostcode); // trigger postcode validation for prefilled value

            console.log('✅ Customer address fields populated:', {
              line1: customerAddress.line1 || customerAddress.address1,
              line2: customerAddress.line2 || customerAddress.address2,
              town: customerAddress.town,
              county: customerAddress.county || customerAddress.country,
              postcode: customerPostcode
            });
          } else {
            console.log('⚠️ No customer address found either');
          }
        }

        // Auto-fill property value - check multiple possible field names
        const propertyValue = lead.conveyancing_details?.propertyDetails?.propertyValue
          || lead.propertyDetails?.propertyValue
          || lead.property_value
          || lead.propertyValue
          || lead.net_value
          || lead.netValue
          || '';
        console.log('💰 Checking for property value:', {
          conveyancing_propertyDetails: lead.conveyancing_details?.propertyDetails?.propertyValue,
          propertyDetails_propertyValue: lead.propertyDetails?.propertyValue,
          property_value: lead.property_value,
          propertyValue: lead.propertyValue,
          net_value: lead.net_value,
          netValue: lead.netValue
        });

        if (propertyValue) {
          setValue('propertyValue', propertyValue);
          console.log('✅ Property value populated:', propertyValue);
        } else {
          console.log('⚠️ No property value found in lead data');
        }

        // Auto-fill Lender Panel
        console.log('🏦 === AUTO-FILLING LENDER PANEL ===');
        const lenderPanelData = lead.conveyancing_details?.lender_panel_id || lead.lender_panel_id;
        const lenderPanelId = lenderPanelData
          ? (typeof lenderPanelData === 'object' ? lenderPanelData._id : lenderPanelData)
          : '';
        console.log('🏦 Lender Panel ID:', lenderPanelId, 'from:', {
          conveyancing_details: lead.conveyancing_details?.lender_panel_id,
          lender_panel_id: lead.lender_panel_id
        });

        if (lenderPanelId) {
          setValue('lender_panel_id', String(lenderPanelId));
          console.log('✅ Lender Panel populated:', lenderPanelId);
        } else {
          console.log('⚠️ No lender panel found in lead data');
        }

        // Partner
        const partnerId = (lead.conveyancing_details?.partner_id || lead.partner_id)
          ? (typeof (lead.conveyancing_details?.partner_id || lead.partner_id) === 'object'
              ? (lead.conveyancing_details?.partner_id || lead.partner_id)._id
              : (lead.conveyancing_details?.partner_id || lead.partner_id))
          : '';
        console.log('🤝 Partner ID:', partnerId);
        if (partnerId) {
          setValue('partner_id', String(partnerId));
        }

        console.log('✅ === AUTO-FILL COMPLETE ===');
      }
    } catch (error) {
      console.error('❌ Error fetching conveyancing details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial customer from URL if customerId is provided (same as Purchase/Sale & Purchase forms)
  useEffect(() => {
    const fetchInitialCustomer = async () => {
      if (customerIdFromUrl && authCtx.token && customers.length === 0) {
        console.log('🔍 Fetching initial customer from URL:', customerIdFromUrl);
        try {
          // Use same endpoint as Purchase component
          const customerRes = await postRequest(`private/getCustomerIndividualData/${customerIdFromUrl}`, {}, authCtx.token);
          if (customerRes?.data) {
            console.log('✅ Fetched initial customer:', customerRes.data);
            setCustomers([customerRes.data]);
          } else {
            console.log('⚠️ No customer found for ID:', customerIdFromUrl);
          }
        } catch (err) {
          console.error('❌ Error fetching initial customer:', err);
        }
      }
    };

    fetchInitialCustomer();
  }, [customerIdFromUrl, authCtx.token]);

  useEffect(() => {
    if (id && id !== 'new' && authCtx.token) {
      fetchConveyancingDetails();
    }
  }, [id, authCtx.token]);

  // Fetch active lender panels on mount
  useEffect(() => {
    fetchActiveLenderPanels();
    fetchPartners();
  }, [authCtx.token]);

  const handleAddCustomer = (customer) => {
    if (!customers.some(c => c._id === customer._id)) {
      setCustomers([...customers, customer]);
    }
  };

  const handleRemoveCustomer = (customerId) => {
    setCustomers(customers.filter(c => c._id !== customerId));
  };

  const onSubmit = async (formData) => {
    // Block save until both postcode checks (UK database + branch match) have passed
    if (checkingPostcode) {
      Swal.fire({
        icon: 'info',
        title: 'Please Wait',
        text: 'Postcode verification is still in progress. Please try again in a moment.'
      });
      return;
    }

    if (!isPostcodeValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Postcode',
        text: 'Please enter a valid UK postcode that is covered by one of our branches before saving.'
      });
      return;
    }

    try {
      const payload = {
        // Data from Step 1 (SimpleConveyancingForm)
        override_date: basicFormData.override_date || new Date(),
        group_code: basicFormData.group_code,
        acquisition: basicFormData.acquisition || 'Direct',
        product_id: basicFormData.product_id,
        service_type: basicFormData.service_type || 'First_Registration',

        customer_ids: customers.map(c => c._id),
        property_address: {
          address1: formData.line1,
          address2: formData.line2,
          town: formData.town,
          country: formData.county,
          postcode: formData.postcode
        },
        property_value: formData.propertyValue,
        ownership_type: formData.ownershipType || null, // Optional for First Registration
        lender_panel_id: formData.lender_panel_id || null,
        partner_id: formData.partner_id || null
      };

      const actualLeadId = id === 'new' ? 'new' : id;
      const res = await postRequest(
        `private/saveConveyancingFirstRegistrationToLead/${actualLeadId}`,
        payload,
        authCtx.token
      );

      if (res?.status === 'success' || res?.status === 200) {
        const savedLeadId = res?.data?.lead_id || actualLeadId;

        // 🎯 Call unified API to calculate and save First Registration fee
        try {
          // ⏱️ Wait 300ms for database to commit flags before calculating fees
          console.log('⏳ Waiting 300ms for database to commit flags...');
          await new Promise(resolve => setTimeout(resolve, 300));

          console.log('💰 Calling calculateAndSaveLeadQuote for First Registration...');
          const feeResponse = await postRequest('private/calculateAndSaveLeadQuote', {
            lead_id: savedLeadId,
            service_type: 'First_Registration',
            product_id: payload.product_id,
            ownership_type: payload.ownership_type,
            property_value: payload.property_value || 0,
            postcode: payload.property_address?.postcode,
            partner_id: payload.partner_id || null
          }, authCtx.token);
          console.log('✅ calculateAndSaveLeadQuote response:', feeResponse);
        } catch (feeError) {
          console.error('⚠️ Error calling calculateAndSaveLeadQuote:', feeError);
          // Non-blocking - don't fail the main operation
        }

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: id === 'new'
            ? 'First Registration created and email sent successfully!'
            : 'First Registration details updated successfully!'
        }).then(() => {
          navigate(`/leads/view/first-registration/${savedLeadId}`);
        });
      } else {
        throw new Error(res?.message || 'Failed to save details');
      }
    } catch (error) {
      console.error('Error saving details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save details'
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit Conveyancing', path: '' },
        ]}
      />
      <br />

      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
            Edit Conveyancing #{conveyancingData?.reference || id} - First Registration
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* First Registration Details Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                First Registration Details
              </Typography>
              
              {/* Property Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Property
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Address</Typography>
                  <Controller
                    name="line1"
                    control={control}
                    rules={{ required: 'Address Line 1 is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 1"
                        variant="outlined"
                        size="small"
                        error={!!errors.line1}
                        helperText={errors.line1?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="line2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 2"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="town"
                    control={control}
                    rules={{ required: 'Town is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Town"
                        variant="outlined"
                        size="small"
                        error={!!errors.town}
                        helperText={errors.town?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="county"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="County"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="postcode"
                    control={control}
                    rules={{ required: 'Postcode is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Postcode"
                        variant="outlined"
                        size="small"
                        error={!!errors.postcode}
                        helperText={errors.postcode?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          setPostcodeValue(e.target.value);
                        }}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />

                  {/* Checking postcode status */}
                  {checkingPostcode && postcodeValue && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      Checking postcode in branches...
                    </Alert>
                  )}

                  {/* Postcode match found */}
                  {postcodeMatch && !checkingPostcode && (
                    <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      ✓ Postcode found in branch: <strong>{postcodeMatch.name}</strong>
                    </Alert>
                  )}

                  {/* Branch selection dropdown */}
                  {!postcodeMatch && availableBranches.length > 0 && !checkingPostcode && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Postcode not found in any branch. Please select a branch:
                      </Alert>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Branch</InputLabel>
                        <Select
                          value={selectedBranch || ''}
                          onChange={(e) => {
                            setSelectedBranch(e.target.value);
                            setIsPostcodeValid(true);
                          }}
                          label="Select Branch"
                        >
                          {availableBranches.map((branch) => (
                            <MenuItem key={branch._id} value={branch._id}>
                              {branch.name} - {branch.address?.postcode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Property Value</Typography>
                    <Controller
                      name="propertyValue"
                      control={control}
                      rules={{ required: 'Property value is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">£</InputAdornment>,
                            }
                          }}
                          error={!!errors.propertyValue}
                          helperText={errors.propertyValue?.message}
                        />
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Customers Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Customers
              </Typography>

              <Box sx={{ mb: 3 }}>
                {customers.map((customer) => (
                  <Box
                    key={customer._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {customer.title} {customer.forename} {customer.surname}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📧 {customer.email}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📞 {customer.phone_number}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => handleRemoveCustomer(customer._id)}
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCustomerModal(true)}
                  sx={{ color: '#6f42c1', mt: 1 }}
                >
                  Add Customer
                </Button>
              </Box>
            </Box>

            {/* Lender Panel Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Lender Panel
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="lender_panel_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Lender Panel (Optional)</em>
                        </MenuItem>
                        {lenderPanels.map((panel) => (
                          <MenuItem key={panel._id} value={panel._id}>
                            {panel.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Partner Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Partner
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="partner_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Partner (Optional)</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={partner._id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={checkingPostcode || !isPostcodeValid}
                sx={{
                  backgroundColor: '#6f42c1',
                  '&:hover': {
                    backgroundColor: '#5a32a3'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      {/* Customer Search Modal */}
      <CustomerSearchModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleAddCustomer}
        selectedCustomers={customers}
      />
    </>
  );
}